import { useState } from 'react';
import { useApplyCatalogAccessChanges } from '../../../../../../../../../services/modules/catalogs/catalogAccess.mutations';
import { useInfiniteCatalogAccessEntities } from '../../../../../../../../../services/modules/catalogs/catalogAccess.queries';
import { CATALOG_ACCESS_ENDPOINT_BY_FIELD } from '../../../../../../../../../services/modules/catalogs/catalogAccess.repository';
import { CatalogTestIds } from '../../../../../../../../../utils/testIds/catalog.testIds';
import { CATALOG_CREATION_ACCESS_TABS } from '../../../../../../../creation/catalogCreationAccess.consts';
import {
  CatalogCreationAccessEntitiesToAdd,
  CatalogCreationAccessFieldName,
  CatalogCreationAccessRowDateChange,
  CatalogCreationAccessTab,
} from '../../../../../../../creation/catalogCreationAccess.types';
import { resolveCatalogCreationAccessEmptyLabel } from '../../../../../../../creation/catalogCreationAccess.utils';
import {
  CatalogAccessCountByField,
  CatalogAccessDates,
  CatalogAccessDraftByField,
  CatalogAccessFieldDraft,
} from '../catalogAccessSection.types';
import {
  EMPTY_CATALOG_ACCESS_DRAFT,
  REMOVING_ALL_CATALOG_ACCESS_FIELD_DRAFT,
  addCatalogAccessDraftEntities,
  buildCatalogAccessChanges,
  buildCatalogAccessRows,
  changeAllCatalogAccessDraftDates,
  changeCatalogAccessDraftDate,
  changeCatalogAccessDraftEntitiesDates,
  countCatalogAccessDraftEntities,
  hasCatalogAccessDraftChanges,
  hasInvalidCatalogAccessDraftDates,
  removeCatalogAccessDraftEntities,
  removeCatalogAccessDraftEntity,
} from '../catalogAccessSection.utils';

interface UseCatalogAccessEditModalParams {
  catalogId: string;
  initialField: CatalogCreationAccessFieldName;
  countByField: CatalogAccessCountByField;
  onClose: () => void;
}

export const useCatalogAccessEditModal = ({
  catalogId,
  initialField,
  countByField,
  onClose,
}: UseCatalogAccessEditModalParams) => {
  const [selectedField, setSelectedField] = useState(initialField);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isSelectingAllResults, setIsSelectingAllResults] = useState(false);
  const [isChangeDatesModalOpen, setIsChangeDatesModalOpen] = useState(false);
  const [draftByField, setDraftByField] = useState<CatalogAccessDraftByField>(
    EMPTY_CATALOG_ACCESS_DRAFT,
  );

  const { query } = useInfiniteCatalogAccessEntities({ catalogId, field: selectedField });
  const { isApplyingChanges, applyChanges } = useApplyCatalogAccessChanges({
    catalogId,
    onSuccess: onClose,
  });

  const draft = draftByField[selectedField];
  const entities = query.pagesData ?? [];

  const rows = buildCatalogAccessRows({
    entities,
    draft,
    nameField: CATALOG_ACCESS_ENDPOINT_BY_FIELD[selectedField].nameField,
  });

  const countOf = (field: CatalogCreationAccessFieldName) =>
    countCatalogAccessDraftEntities({
      draft: draftByField[field],
      serverCount: countByField[field],
    });

  const tabs: CatalogCreationAccessTab[] = CATALOG_CREATION_ACCESS_TABS.map(({ field, label }) => ({
    id: field,
    label,
    count: countOf(field),
    testId: `${CatalogTestIds.CATALOG_CREATION_MODAL_ACCESS_TAB_PREFIX}${field}`,
  }));

  const updateDraft = (
    updateFieldDraft: (draft: CatalogAccessFieldDraft) => CatalogAccessFieldDraft,
  ) => {
    setDraftByField((currentDraftByField) => ({
      ...currentDraftByField,
      [selectedField]: updateFieldDraft(currentDraftByField[selectedField]),
    }));
  };

  const clearSelection = () => {
    setSelectedIds([]);
    setIsSelectingAllResults(false);
  };

  const handleSelectTab = (tabId: string) => {
    const selectedTab = CATALOG_CREATION_ACCESS_TABS.find(({ field }) => field === tabId);

    if (!selectedTab) {
      return;
    }

    clearSelection();
    setSelectedField(selectedTab.field);
  };

  const handleChangeRowDate = ({
    entityId,
    dateField,
    date,
  }: CatalogCreationAccessRowDateChange) => {
    const currentRow = rows.find((row) => row.id === entityId);

    if (!currentRow) {
      return;
    }

    updateDraft((currentDraft) =>
      changeCatalogAccessDraftDate({
        draft: currentDraft,
        entityId,
        dateField,
        date,
        currentDates: { startDate: currentRow.startDate, endDate: currentRow.endDate },
      }),
    );
  };

  const handleRemoveRow = (entityId: string) => {
    setSelectedIds((currentSelectedIds) =>
      currentSelectedIds.filter((selectedId) => selectedId !== entityId),
    );
    updateDraft((currentDraft) => removeCatalogAccessDraftEntity(currentDraft, entityId));
  };

  const handleToggleSelectRow = (entityId: string) => {
    setSelectedIds((currentSelectedIds) =>
      currentSelectedIds.includes(entityId)
        ? currentSelectedIds.filter((selectedId) => selectedId !== entityId)
        : [...currentSelectedIds, entityId],
    );
  };

  const handleToggleSelectAll = () => {
    setSelectedIds((currentSelectedIds) =>
      currentSelectedIds.length === rows.length ? [] : rows.map((row) => row.id),
    );
  };

  const handleToggleSelectAllResults = () => {
    setIsSelectingAllResults((isSelecting) => !isSelecting);
    setSelectedIds([]);
  };

  const handleRemoveSelected = () => {
    updateDraft((currentDraft) =>
      isSelectingAllResults
        ? REMOVING_ALL_CATALOG_ACCESS_FIELD_DRAFT
        : removeCatalogAccessDraftEntities(currentDraft, selectedIds),
    );
    clearSelection();
  };

  const handleChangeSelectedDates = (dates: CatalogAccessDates) => {
    updateDraft((currentDraft) =>
      isSelectingAllResults
        ? changeAllCatalogAccessDraftDates(currentDraft, dates)
        : changeCatalogAccessDraftEntitiesDates({
            draft: currentDraft,
            entityIds: selectedIds,
            dates,
          }),
    );
    setIsChangeDatesModalOpen(false);
    clearSelection();
  };

  const handleAddEntities = ({
    entityIds,
    entityNames,
    startDate,
    endDate,
  }: CatalogCreationAccessEntitiesToAdd) => {
    updateDraft((currentDraft) =>
      addCatalogAccessDraftEntities({
        draft: currentDraft,
        entityIds,
        entityNames,
        startDate,
        endDate,
      }),
    );
  };

  const handleApply = () => {
    applyChanges(buildCatalogAccessChanges(draftByField));
  };

  const hasNoEntities = CATALOG_CREATION_ACCESS_TABS.every(({ field }) => countOf(field) === 0);

  const isApplyDisabled =
    !hasCatalogAccessDraftChanges(draftByField) || hasInvalidCatalogAccessDraftDates(draftByField);

  const selectedRowIds = isSelectingAllResults ? rows.map((row) => row.id) : selectedIds;

  return {
    tabs,
    rows,
    selectedField,
    selectedRowIds,
    isSelectingAllResults,
    isChangeDatesModalOpen,
    totalSelected: isSelectingAllResults ? countOf(selectedField) : selectedIds.length,
    totalResults: countOf(selectedField),
    isAllSelected: isSelectingAllResults || (rows.length > 0 && selectedIds.length === rows.length),
    isApplyingChanges,
    isApplyDisabled,
    listedEntityIds: rows.map((row) => row.id),
    emptyLabel: query.isLoading
      ? null
      : resolveCatalogCreationAccessEmptyLabel({ hasNoEntities, rowCount: rows.length }),
    clearSelection,
    handleSelectTab,
    handleChangeRowDate,
    handleRemoveRow,
    handleToggleSelectRow,
    handleToggleSelectAll,
    handleToggleSelectAllResults,
    handleRemoveSelected,
    handleChangeSelectedDates,
    handleOpenChangeDatesModal: () => setIsChangeDatesModalOpen(true),
    handleCloseChangeDatesModal: () => setIsChangeDatesModalOpen(false),
    handleAddEntities,
    handleApply,
    handleEndReached: query.fetchNextPage,
  };
};
